import { CollaboratingGroupContext, CountryContext, EventContext, EventDefinitionContext, FormContext, FormCreateContext, FormDefinitionContext, FormDefinitionProvider, InstitutionContext, LookupsContext, MasterGroupContext, PatientContext, PatientStateContext, TreatmentContext, useGenerateEventDefinitionContext, useGeneratePatientStateContext, useGenerateTreatmentContext } from '@ngt/opms';
import { ComponentType, FC, PropsWithChildren, useContext, useMemo } from 'react';
import useGenerateEConsentCollaboratingGroupContext from '../../../hooks/context/useGenerateEConsentCollaboratingGroupContext';
import useGenerateEConsentCountryContext from '../../../hooks/context/useGenerateEConsentCountryContext';
import useGenerateEConsentEventContext from '../../../hooks/context/useGenerateEConsentEventContext';
import useGenerateEConsentFormContext from '../../../hooks/context/useGenerateEConsentFormContext';
import useGenerateEConsentInstitutionContext from '../../../hooks/context/useGenerateEConsentInstitutionContext';
import useGenerateEConsentLookupsContext from '../../../hooks/context/useGenerateEConsentLookupsContext';
import useGenerateEConsentMasterGroupContext from '../../../hooks/context/useGenerateEConsentMasterGroupContext';
import useGenerateEConsentPatientContext from '../../../hooks/context/useGenerateEConsentPatientContext';
import useConsentEventData from '../../../hooks/data/useConsentEventData';
import EConsentContext, { EConsentUserType, IEConsentContext } from '../../../contexts/EConsentContext';


export interface IEConsentByGuidProviderProps extends PropsWithChildren {
    guid?: string;
    formDefinitionIdentifier?: string | number;
    userType: EConsentUserType;
    loadBeforeMount?: boolean;
    LoadingComponent?: ComponentType<any>;
}


export const InternalEConsentByGuidProvider: FC<IEConsentByGuidProviderProps> = ({formDefinitionIdentifier, userType, guid, children, loadBeforeMount, LoadingComponent }) => {
    const formDefinitionContext = useContext(FormDefinitionContext);

    const consentEventContext = useConsentEventData(guid);
    
    const masterGroupContext = useGenerateEConsentMasterGroupContext(consentEventContext);
    const collaboratingGroupContext = useGenerateEConsentCollaboratingGroupContext(consentEventContext);
    const countryContext = useGenerateEConsentCountryContext(consentEventContext);
    const institutionContext = useGenerateEConsentInstitutionContext(consentEventContext);
    const patientContext = useGenerateEConsentPatientContext(consentEventContext);
    const patientStateContext = useGeneratePatientStateContext(patientContext.data?.patientStateId);
    const treatmentContext = useGenerateTreatmentContext(patientContext.data?.treatmentId);
    const eventContext = useGenerateEConsentEventContext(consentEventContext);

    const eventDefinitionContext = useGenerateEventDefinitionContext(eventContext.data?.eventDefinitionId)

    const formContext = useGenerateEConsentFormContext(formDefinitionContext.data, consentEventContext);
    const lookupsContext = useGenerateEConsentLookupsContext(formDefinitionContext.data, consentEventContext);

    const formCreateContext = useMemo(() => {
        return {
            createPatient: false,
            createEvent: false,
            createForm: false
        }
    }, [])

    const eCosentContext: IEConsentContext = useMemo(() => {
        return {
            consentEvent: consentEventContext.data?.consentEvent,
            consentForms: consentEventContext.data?.consentEventEConsentForms,
            investigators: consentEventContext.data?.investigators,
            userType: userType
        }
    }, [consentEventContext, userType])

    const loading = (
        patientContext.loading ||
        (patientContext.data?.patientStateId && patientStateContext.loading) ||
        (patientContext.data?.treatmentId && treatmentContext.loading) ||
        (patientContext.data?.institutionId && institutionContext.loading) ||
        (institutionContext.data?.masterGroupId && masterGroupContext.loading) ||
        (institutionContext.data?.collaboratingGroupId && collaboratingGroupContext.loading) ||
        (institutionContext.data?.countryId && countryContext.loading) ||
        eventDefinitionContext.loading ||
        (formDefinitionIdentifier && formDefinitionContext.loading) ||
        eventContext.loading ||
        (formDefinitionIdentifier && formContext.loading) ||
        (formDefinitionIdentifier && lookupsContext.loading)
    );

    return (
        <EventDefinitionContext.Provider value={eventDefinitionContext}>
            <MasterGroupContext.Provider value={masterGroupContext}>
                <CollaboratingGroupContext.Provider value={collaboratingGroupContext}>
                    <CountryContext.Provider value={countryContext}>
                        <InstitutionContext.Provider value={institutionContext}>
                            <PatientContext.Provider value={patientContext}>
                                <PatientStateContext.Provider value={patientStateContext}>
                                    <TreatmentContext.Provider value={treatmentContext}>
                                        <EventContext.Provider value={eventContext}>
                                            <FormCreateContext.Provider value={formCreateContext}>
                                                <FormContext.Provider value={formContext as any}>
                                                    <LookupsContext.Provider value={lookupsContext}>
                                                        <EConsentContext.Provider value={eCosentContext}>
                                                            {
                                                                loadBeforeMount && loading ? 
                                                                    LoadingComponent ?
                                                                        <LoadingComponent /> :
                                                                        "Loading" :
                                                                    children
                                                            }
                                                        </EConsentContext.Provider>
                                                    </LookupsContext.Provider>
                                                </FormContext.Provider>
                                            </FormCreateContext.Provider>
                                        </EventContext.Provider>
                                    </TreatmentContext.Provider>
                                </PatientStateContext.Provider>        
                            </PatientContext.Provider>
                        </InstitutionContext.Provider>
                    </CountryContext.Provider>
                </CollaboratingGroupContext.Provider>
            </MasterGroupContext.Provider>
        </EventDefinitionContext.Provider>
    );
}

export const EConsentByGuidProvider: FC<IEConsentByGuidProviderProps> = ({ children, ...props }) => {
    return (
        <FormDefinitionProvider
            formDefinitionIdentifier={props.formDefinitionIdentifier}
        >
            <InternalEConsentByGuidProvider
                {...props}
            >
                {children}
            </InternalEConsentByGuidProvider>
        </FormDefinitionProvider>
    );
}

export default EConsentByGuidProvider;
